import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  MenuItem,
  Typography,
  Chip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PageHeader from '../../components/PageHeader';
import DataState from '../../components/DataState';
import { useSession } from '../../permissions/SessionContext';
import { hasPermissions } from '../../permissions/check';
import {
  fetchStudents,
  createStudent,
  updateStudent,
  fetchClasses,
  registerTuitionBilling,
  cancelTuitionBilling,
  fetchTuitionBillings,
  fetchStudentContact,
} from '../../api/academy';
import { academyScope } from './scope';
import { colors, layout } from '../../design-system/tokens';

const won = (n) => `${Number(n || 0).toLocaleString('ko-KR')}원`;

const STATUS_LABELS = { active: '재원', paused: '휴원', withdrawn: '퇴원' };
const STATUS_STYLE = {
  active: colors.status.success,
  paused: colors.status.warning,
  withdrawn: colors.status.neutral,
};
const BILLING_STATUS_LABELS = {
  pending: { label: '보호자 수락 대기', next: '보호자 앱 수락을 기다리세요.' },
  active: { label: '등록 완료', next: '실청구 준비 상태는 결제 관리에서 확인하세요.' },
  paused: { label: '청구 보류', next: '결제 이력의 사유와 다음 확인 항목을 보세요.' },
  rejected: { label: '보호자 거절', next: '연락처·연결 대상을 확인한 뒤 다시 요청하세요.' },
  cancelled: { label: '해지됨', next: '필요하면 보호자 수락부터 다시 요청하세요.' },
};

const entityId = (value) => String(value?.id ?? value?._id ?? value ?? '');
const maskedPhone = (row) => {
  if (row?.guardianPhoneMasked) return row.guardianPhoneMasked;
  const digits = String(row?.guardianPhone ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length < 7) return '*'.repeat(digits.length);
  return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
};

const emptyForm = {
  name: '',
  grade: '',
  classIds: [],
  guardianName: '',
  guardianPhone: '',
  memo: '',
};

const Students = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  const canManage = hasPermissions(session, ['students.manage']);
  const canManageTuition = hasPermissions(session, ['tuition.manage']);
  const canReadTuition = hasPermissions(session, ['tuition.read']);
  const canReadParents = hasPermissions(session, ['parents.read']);
  const canViewContact = hasPermissions(session, ['parents.manage']);

  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [query, setQuery] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [billingTarget, setBillingTarget] = useState(null);
  const [billingDay, setBillingDay] = useState('5');
  const [billingClassId, setBillingClassId] = useState('');
  const [billingError, setBillingError] = useState('');
  const [chargeEnabled, setChargeEnabled] = useState(false);
  const [billingItems, setBillingItems] = useState([]);
  const [billingStatusLoading, setBillingStatusLoading] = useState(canReadTuition);
  const [billingStatusError, setBillingStatusError] = useState('');
  const [loadingContactId, setLoadingContactId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (query.trim()) params.q = query.trim();
      const scope = academyScope(session);
      const [students, classList] = await Promise.all([
        fetchStudents({ ...params, ...scope }),
        fetchClasses(scope),
      ]);
      setRows(students);
      setClasses(classList);
    } catch (e) {
      setError(e?.response?.data?.message || '원생 목록을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, query, session]);

  useEffect(() => {
    load();
  }, [load]);

  // 학원비 실 과금 플래그 상태 — 원장에게 "아직 실제 청구는 나가지 않는다"를 알리기 위함.
  // tuition.read 권한이 없는 직원은 호출하지 않는다 (403 방지).
  const loadBillingStatus = useCallback(async () => {
    if (!canReadTuition) {
      setBillingItems([]);
      setBillingStatusError('');
      setBillingStatusLoading(false);
      return;
    }
    setBillingStatusLoading(true);
    setBillingStatusError('');
    try {
      const res = await fetchTuitionBillings(academyScope(session));
      setChargeEnabled(Boolean(res?.chargeEnabled));
      setBillingItems(Array.isArray(res?.items) ? res.items : []);
    } catch {
      // 목록을 모르면 이미 등록된 반을 다시 등록하거나 잘못 해지할 수 있다.
      // 청구 실행은 하지 않지만 준비 기록도 상태를 다시 읽을 때까지 잠근다.
      setBillingItems([]);
      setChargeEnabled(false);
      setBillingStatusError(
        '학원비 청구 준비 상태를 확인하지 못했어요. 중복 등록이나 잘못된 해지를 막기 위해 변경을 잠시 멈췄어요.',
      );
    } finally {
      setBillingStatusLoading(false);
    }
  }, [canReadTuition, session]);

  useEffect(() => {
    loadBillingStatus();
  }, [loadBillingStatus]);

  const canManageBilling =
    canManage && canManageTuition && !billingStatusLoading && !billingStatusError;

  const classMap = useMemo(
    () => new Map(classes.map((c) => [entityId(c), c])),
    [classes],
  );
  const capacityWarnings = form.classIds
    .map((classId) => classMap.get(entityId(classId)))
    .filter((academyClass) => {
      if (!academyClass?.capacity || Number(academyClass.studentCount || 0) < Number(academyClass.capacity)) {
        return false;
      }
      const wasAlreadyAssigned = (editing?.classIds ?? []).some(
        (assignedClass) => entityId(assignedClass) === entityId(academyClass),
      );
      return !wasAlreadyAssigned;
    });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = async (row) => {
    const studentId = entityId(row);
    if (!studentId || !canViewContact || loadingContactId) return;
    setLoadingContactId(studentId);
    setError('');
    try {
      const contact = await fetchStudentContact(studentId, academyScope(session));
      setEditing(row);
      setForm({
        name: row.name ?? '',
        grade: row.grade ?? '',
        classIds: (row.classIds ?? []).map(entityId),
        guardianName: contact?.guardianName ?? row.guardianName ?? '',
        guardianPhone: contact?.guardianPhone ?? '',
        memo: contact?.memo ?? '',
      });
      setFormError('');
      setDialogOpen(true);
    } catch (e) {
      setError(e?.response?.data?.message || '보호자 연락처 상세를 불러오지 못했어요.');
    } finally {
      setLoadingContactId('');
    }
  };

  const submit = async () => {
    setSaving(true);
    setFormError('');
    try {
      const scope = academyScope(session);
      if (editing) await updateStudent(entityId(editing), { ...form, ...scope });
      else await createStudent({ ...form, ...scope });
      setDialogOpen(false);
      await load();
    } catch (e) {
      setFormError(e?.response?.data?.message || '저장하지 못했어요.');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (row, status) => {
    try {
      await updateStudent(entityId(row), { status, ...academyScope(session) });
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || '상태를 바꾸지 못했어요.');
    }
  };

  /** 아직 자동결제가 걸리지 않은 반 — 반 단위로 등록하므로 남은 반만 고를 수 있게 한다 */
  const billingsFor = (row) => {
    const references = new Set(row?.tuitionSummary?.billingReferences ?? []);
    const fromEndpoint = billingItems.filter(
      (billing) => references.has(billing.reference),
    );
    return fromEndpoint.length > 0 ? fromEndpoint : row?.billings ?? [];
  };

  const unbilledClasses = (row) => {
    const billed = new Set(billingsFor(row).map((b) => entityId(b.classId)));
    return (row?.classIds ?? []).filter((c) => !billed.has(entityId(c)));
  };

  const openBilling = (row) => {
    const remaining = unbilledClasses(row);
    setBillingTarget(row);
    setBillingClassId(remaining.length === 1 ? entityId(remaining[0]) : '');
    setBillingDay('5');
    setBillingError('');
  };

  const submitBilling = async () => {
    setBillingError('');
    try {
      await registerTuitionBilling({
        studentId: entityId(billingTarget),
        classId: billingClassId || undefined,
        billingDay: Number(billingDay),
        ...academyScope(session),
      });
      setBillingTarget(null);
      await load();
    } catch (e) {
      setBillingError(
        e?.response?.data?.message || '자동결제를 등록하지 못했어요.',
      );
    }
  };

  const stopBilling = async (billingId) => {
    try {
      await cancelTuitionBilling(billingId, academyScope(session));
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || '자동결제를 해지하지 못했어요.');
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <PageHeader
          title="수강생 관리"
          description="원생 정보와 학원비 자동결제를 관리해요. 원생 개인정보는 최소한만 수집합니다 (생년월일·본인 연락처 미수집)."
        />
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={openCreate}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            원생 등록
          </Button>
        )}
      </Box>

      {canReadTuition && !chargeEnabled && (
        <Alert severity="info" sx={{ mb: layout.sectionGap }}>
          학원비 자동결제는 현재 <b>준비 모드</b>예요. 등록해 두면 청구 대상으로 잡히지만,
          실제 카드 승인은 아직 나가지 않습니다.
        </Alert>
      )}

      {canReadTuition && billingStatusError && (
        <Alert
          severity="warning"
          sx={{ mb: layout.sectionGap }}
          action={
            <Button color="inherit" size="small" onClick={loadBillingStatus}>
              상태 다시 확인
            </Button>
          }
        >
          {billingStatusError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: layout.cardGap, mb: layout.sectionGap }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(e, v) => v && setStatusFilter(v)}
        >
          <ToggleButton value="active">재원</ToggleButton>
          <ToggleButton value="paused">휴원</ToggleButton>
          <ToggleButton value="withdrawn">퇴원</ToggleButton>
          <ToggleButton value="all">전체</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="이름 검색"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 180 }}
        />
      </Box>

      <DataState
        loading={loading}
        error={error}
        onRetry={load}
        empty={rows.length === 0}
        emptyText="조건에 맞는 원생이 없어요."
      >
        <Paper sx={{ p: 1 }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>이름</TableCell>
                  <TableCell>학년</TableCell>
                  <TableCell>반</TableCell>
                  {canReadParents && <TableCell>보호자</TableCell>}
                  <TableCell>상태</TableCell>
                  {canReadTuition && <TableCell>학원비 자동결제</TableCell>}
                  {canManage && <TableCell align="right">관리</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const enrolled = (row.classIds ?? []).map(
                    (c) => classMap.get(entityId(c)) || c,
                  );
                  const st = STATUS_STYLE[row.status] || colors.status.neutral;
                  const billings = billingsFor(row);
                  const remaining = unbilledClasses(row);
                  return (
                    <TableRow key={entityId(row)} hover>
                      {/* 이름·학년은 짧으므로 줄바꿈 대신 한 줄 유지 (좁으면 표가 가로 스크롤) */}
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {row.name}
                      </TableCell>
                      <TableCell sx={{ color: colors.text.muted, whiteSpace: 'nowrap' }}>
                        {row.grade || '-'}
                      </TableCell>
                      <TableCell sx={{ color: colors.text.muted }}>
                        {enrolled.length === 0 ? (
                          '미배정'
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {enrolled.map((c) => (
                              <Chip
                                key={entityId(c)}
                                size="small"
                                label={c?.name ?? '-'}
                                sx={{
                                  bgcolor: colors.brand.primarySoft,
                                  color: colors.brand.primary,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      {canReadParents && (
                        <TableCell sx={{ color: colors.text.muted, whiteSpace: 'nowrap' }}>
                          {row.guardianName || '-'}
                          {maskedPhone(row) ? (
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', color: colors.text.muted }}
                            >
                              {maskedPhone(row)}
                            </Typography>
                          ) : null}
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABELS[row.status] || '상태 확인 필요'}
                          sx={{ bgcolor: st.bg, color: st.fg }}
                        />
                      </TableCell>
                      {canReadTuition && (
                        <TableCell>
                          {billings.length === 0 ? (
                            <Typography variant="caption" sx={{ color: colors.text.muted }}>
                              미등록
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {/* 반이 여러 개면 반별로 각각 청구된다 */}
                            {billings.map((b) => (
                              <Box
                                key={entityId(b)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {won(b.amount)}
                                </Typography>
                                <Box>
                                  <Typography variant="caption" sx={{ color: colors.text.muted }}>
                                    {b.className}
                                    {BILLING_STATUS_LABELS[b.status]
                                      ? ` · ${BILLING_STATUS_LABELS[b.status].label}`
                                      : ' · 상태 확인 필요'}
                                  </Typography>
                                  {BILLING_STATUS_LABELS[b.status]?.next && (
                                    <Typography
                                      variant="caption"
                                      sx={{ display: 'block', color: colors.text.muted }}
                                    >
                                      다음: {BILLING_STATUS_LABELS[b.status].next}
                                    </Typography>
                                  )}
                                </Box>
                                {canManageBilling && (
                                  <Button
                                    size="small"
                                    color="error"
                                    sx={{ minWidth: 'auto', px: 0.75 }}
                                  onClick={() => stopBilling(entityId(b))}
                                  >
                                    해지
                                  </Button>
                                )}
                              </Box>
                            ))}
                            {billings.length > 1 && (
                              <Typography
                                variant="caption"
                                sx={{ color: colors.text.secondary, fontWeight: 700 }}
                              >
                                합계 {won(row.tuitionSummary?.monthlyExpectedAmount ?? row.billingTotal)}
                              </Typography>
                            )}
                            </Box>
                          )}
                        </TableCell>
                      )}
                      {canManage && (
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              justifyContent: 'flex-end',
                              flexWrap: 'wrap',
                            }}
                          >
                            {canViewContact && (
                              <Button
                                size="small"
                                onClick={() => openEdit(row)}
                                disabled={Boolean(loadingContactId)}
                              >
                                {loadingContactId === entityId(row) ? '불러오는 중…' : '수정'}
                              </Button>
                            )}
                            {row.status === 'active' && (
                              <Button size="small" onClick={() => changeStatus(row, 'paused')}>
                                휴원
                              </Button>
                            )}
                            {row.status !== 'active' && (
                              <Button size="small" onClick={() => changeStatus(row, 'active')}>
                                복원
                              </Button>
                            )}
                            {/* 아직 청구가 안 걸린 반이 남아 있으면 추가 등록할 수 있다 */}
                            {canManageBilling &&
                              row.status !== 'withdrawn' &&
                              remaining.length > 0 && (
                                <Button size="small" onClick={() => openBilling(row)}>
                                  {billings.length > 0 ? '결제추가' : '결제등록'}
                                </Button>
                              )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DataState>

      {!loading && !error && rows.length === 0 && canManage && (
        <Alert severity="info" sx={{ mt: layout.cardGap }}>
          반을 먼저 만든 뒤 원생을 등록하면 보호자 초대와 출결 기록을 이어서 진행할 수 있어요.
          <Button size="small" onClick={() => navigate('/owner-v2/classes')} sx={{ ml: 1 }}>
            수업 관리 열기
          </Button>
        </Alert>
      )}

      {/* 원생 등록/수정 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? '원생 정보 수정' : '원생 등록'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="원생 이름"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            autoFocus
          />
          <TextField
            label="학년"
            value={form.grade}
            onChange={(e) => setForm({ ...form, grade: e.target.value })}
            placeholder="중2"
            helperText="생년월일 대신 학년만 받습니다 (미성년자 개인정보 최소 수집)"
            fullWidth
          />
          <TextField
            select
            label="수강 반"
            value={form.classIds}
            onChange={(e) =>
              setForm({
                ...form,
                classIds:
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value,
              })
            }
            helperText="여러 반을 동시에 수강할 수 있어요. 학원비는 반마다 따로 청구됩니다. 정원 도달 반도 경고 후 배정할 수 있습니다."
            slotProps={{
              select: {
                multiple: true,
                renderValue: (selected) =>
                  selected.length === 0
                    ? '미배정'
                    : selected
                        .map((id) => classes.find((c) => entityId(c) === id)?.name ?? id)
                        .join(', '),
              },
            }}
            fullWidth
          >
            {classes.map((c) => (
              <MenuItem key={entityId(c)} value={entityId(c)}>
                {c.name} · {won(c.tuitionFee)}
              </MenuItem>
            ))}
          </TextField>
          {capacityWarnings.length > 0 && (
            <Alert severity="warning">
              선택한 반은 정원에 도달했거나 초과했습니다. 이번 배정은 저장할 수 있으며 대기 명단은
              만들지 않습니다.
              {capacityWarnings.map((academyClass) => (
                <Typography key={entityId(academyClass)} variant="body2" sx={{ mt: 0.5 }}>
                  {academyClass.name}: 재원 {academyClass.studentCount}명 / 정원 {academyClass.capacity}명
                </Typography>
              ))}
            </Alert>
          )}
          <TextField
            label="보호자 이름"
            value={form.guardianName}
            onChange={(e) => setForm({ ...form, guardianName: e.target.value })}
            fullWidth
          />
          <TextField
            label="보호자 연락처"
            value={form.guardianPhone}
            onChange={(e) => setForm({ ...form, guardianPhone: e.target.value })}
            placeholder="010-0000-0000"
            fullWidth
          />
          <TextField
            label="메모"
            value={form.memo}
            onChange={(e) => setForm({ ...form, memo: e.target.value })}
            helperText="건강·가정사 등 민감정보는 입력하지 마세요"
            multiline
            minRows={2}
            fullWidth
          />
          {formError && (
          <Typography role="alert" variant="body2" sx={{ color: colors.status.danger.fg }}>
            {formError}
          </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 학원비 자동결제 등록 */}
      <Dialog
        open={Boolean(billingTarget)}
        onClose={() => setBillingTarget(null)}
        fullWidth
        maxWidth="xs"
      >
      <DialogTitle>학원비 자동결제 등록</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <DialogContentText sx={{ fontSize: 14 }}>
            <b>{billingTarget?.name}</b> 원생의 학원비 청구 대상과 보호자 동의 연결을 준비합니다.
            현재 준비 모드에서는 카드 승인이나 실제 청구가 나가지 않으며, 운영·법무 게이트가
            모두 확인되기 전에는 실행되지 않습니다.
          </DialogContentText>
          <TextField
            select
            label="청구할 반"
            value={billingClassId}
            onChange={(e) => setBillingClassId(e.target.value)}
            helperText="반마다 따로 등록해요. 여러 반을 들으면 각각 청구됩니다."
            fullWidth
          >
            {unbilledClasses(billingTarget).map((c) => {
              const id = entityId(c);
              const info = classMap.get(id) || c;
              return (
                <MenuItem key={id} value={id}>
                  {info?.name ?? id}
                  {info?.tuitionFee != null ? ` · ${won(info.tuitionFee)}` : ''}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            select
            label="매월 청구일"
            value={billingDay}
            onChange={(e) => setBillingDay(e.target.value)}
            helperText="말일 처리가 달마다 갈려 1~28일 중에서만 고를 수 있어요"
            fullWidth
          >
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
              <MenuItem key={d} value={String(d)}>
                매월 {d}일
              </MenuItem>
            ))}
          </TextField>
          {billingError && (
            <Typography role="alert" variant="body2" sx={{ color: colors.status.danger.fg }}>
              {billingError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBillingTarget(null)}>취소</Button>
          <Button variant="contained" onClick={submitBilling} disabled={!billingClassId}>
            등록
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Students;
